import React, { ReactNode, useMemo, useEffect, useRef } from 'react'
import {
  inspectorHoveredElementContext,
  inspectorContext,
  useInspectorOverlayEvents,
  InspectorContext,
  useHasAnyLayerWithExportableAssets,
} from '../../hooks/inspector'
import { useGetActiveSidebarTab } from '../../../components/ShareSidebarTabContext'
import { useSketchSceneTree } from '../../hooks/inspector/useSketchSceneTree'
import { useCanvasInteractions, useCanvasSelection } from '@sketch/canvas'
import {
  findSketchElementFromUuid,
  ColorFormatProvider,
  InspectorHostProvider,
} from '@sketch/inspector'
import { useComponentsState } from '../../../components/ComponentsStateContext'
import { useVersioning } from '../../../../versioning'
import { SymbolPathLinkDropdown } from '../SymbolPathLinkDropdown'
import { InspectorComponentLink } from '../InspectorComponentLink'
import { InspectCommandBarRegistration } from '../../../commandBarCommands/InspectCommandBarRegistration'
import { useCollapseSelectionOnDismiss } from './useCollapseSelectionOnDismiss'

type FrameInspectorProviderProps = {
  children: ReactNode
  /**
   * Automatically select the top-level frame element when the inspector is active.
   * This can only work if the root is a top-level frame element (frame view).
   */
  autoSelectRootInitially: boolean
  /**
   * When set, the element with this UUID is used as the scene root for the inspector
   * instead of the actual scene root. This allows scoping the inspector layer tree
   * to a specific frame without switching the canvas to frame-only mode.
   */
  rootElementUUID?: string
}

export function FrameInspectorProvider({
  children,
  autoSelectRootInitially,
  rootElementUUID,
}: FrameInspectorProviderProps) {
  const activeSidebarTab = useGetActiveSidebarTab()
  const { selectedElement, selectElement: setSelectedElement } =
    useCanvasSelection()
  const componentsState = useComponentsState()
  const versioning = useVersioning()
  const isInspectorActive = activeSidebarTab === 'Inspect'

  const { sketchRootElement, hasFailed, isTreeFullyLoaded } =
    useSketchSceneTree(isInspectorActive)

  // When rootElementUUID is set, scope the inspector to that element's subtree.
  // This lets the layer tree show only the children of a specific frame
  // without switching the canvas to frame-only mode.
  const inspectorRootElement = useMemo(() => {
    if (!rootElementUUID || !sketchRootElement) return sketchRootElement
    return (
      findSketchElementFromUuid(sketchRootElement, rootElementUUID) ??
      sketchRootElement
    )
  }, [rootElementUUID, sketchRootElement])

  const hasAnyLayerWithExportableAssets =
    useHasAnyLayerWithExportableAssets(inspectorRootElement)

  const { isEnabled: isCanvasInteractionsEnabled } = useCanvasInteractions()

  // Enable inspector interactions only when inspector is active and canvas interactions are enabled
  const enableInspectorInteractions =
    isInspectorActive && isCanvasInteractionsEnabled

  const {
    hoveredElement,
    setHoveredElement,
    clickHandler,
    mouseMoveHandler,
    mouseDownHandle,
  } = useInspectorOverlayEvents(
    inspectorRootElement,
    enableInspectorInteractions,
    autoSelectRootInitially,
    isTreeFullyLoaded
  )

  // The inspector selection lives in React state that outlives a change of
  // `rootElementUUID` (e.g. switching between Sections via the breadcrumb),
  // because the provider isn't remounted. The previously selected element then
  // belongs to the old subtree, so its selection guides would linger on the new
  // root. Clear the selection whenever the scoped root changes so the new root
  // auto-selects instead. The ref skips the initial mount, where clearing would
  // stomp a `?layerPath=` deep-link restore.
  const previousRootElementUUID = useRef(rootElementUUID)
  useEffect(() => {
    if (previousRootElementUUID.current === rootElementUUID) return
    previousRootElementUUID.current = rootElementUUID
    setSelectedElement(null)
  }, [rootElementUUID, setSelectedElement])

  // The inspector package looks components up against the document being
  // viewed; which document and which version is the application's business.
  const inspectorHostDocument = useMemo(
    () => ({
      shareIdentifier: versioning.share.identifier,
      currentVersion: versioning.loading
        ? undefined
        : versioning.currentVersion,
    }),
    [versioning]
  )

  let inspectorStatus: InspectorContext['inspectorStatus'] = 'pending'

  if (inspectorRootElement) {
    inspectorStatus = 'ready' as const
  } else if (hasFailed) {
    inspectorStatus = 'errored' as const
  }

  useCollapseSelectionOnDismiss(isInspectorActive)

  const inspectorContextValue = useMemo((): InspectorContext => {
    return {
      isInspectorActive: isInspectorActive,
      sketchSceneRootElement: inspectorRootElement,
      setHoveredElement,
      inspectorStatus,
      hasAnyLayerWithExportableAssets,
      clickHandler,
      mouseMoveHandler,
      mouseDownHandle,
      isTreeFullyLoaded,
    }
  }, [
    isInspectorActive,
    inspectorRootElement,
    setHoveredElement,
    inspectorStatus,
    hasAnyLayerWithExportableAssets,
    clickHandler,
    mouseMoveHandler,
    mouseDownHandle,
    isTreeFullyLoaded,
  ])

  return (
    <inspectorContext.Provider value={inspectorContextValue}>
      <inspectorHoveredElementContext.Provider value={hoveredElement}>
        <ColorFormatProvider>
          <InspectorHostProvider
            isIngestingComponents={componentsState === 'PROCESSING'}
            symbolPathDropdown={SymbolPathLinkDropdown}
            componentLink={InspectorComponentLink}
            document={inspectorHostDocument}
          >
            {isInspectorActive && selectedElement && (
              <InspectCommandBarRegistration />
            )}
            {children}
          </InspectorHostProvider>
        </ColorFormatProvider>
      </inspectorHoveredElementContext.Provider>
    </inspectorContext.Provider>
  )
}
